import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { applyMode, Mode } from '@amzn/awsui-global-styles';

/**
 * Theme Context
 *
 * Manages visual mode (light/dark) state, persists preference to localStorage,
 * detects system color scheme on first visit, and applies Cloudscape mode.
 *
 * Requirements: 1.1–1.6, 2.1–2.3, 4.1–4.4, 5.1–5.2, 6.1–6.4
 */

export type VisualMode = 'light' | 'dark';

export interface ThemeContextValue {
  mode: VisualMode;
  toggleMode: () => void;
}

const STORAGE_KEY = 'theme-mode';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Resolve the initial visual mode on first render.
 *
 * Priority: localStorage (valid) → system preference → light fallback.
 * Invalid localStorage values are overwritten with "light".
 */
function resolveInitialMode(): VisualMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    if (stored !== null) {
      // Invalid value — overwrite with default
      localStorage.setItem(STORAGE_KEY, 'light');
      return 'light';
    }
  } catch {
    // localStorage unavailable (private browsing) — fall through
  }

  // No stored preference — check system preference
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch {
    // matchMedia unavailable — fall through
  }

  return 'light';
}

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Provider Component
 *
 * Applies Cloudscape visual mode and synchronizes body background color
 * using the colorBackgroundLayoutMain design token on every mode change.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setMode] = useState<VisualMode>(resolveInitialMode);

  useEffect(() => {
    applyMode(mode === 'dark' ? Mode.Dark : Mode.Light);

    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage unavailable — preference won't persist this session
    }
  }, [mode]);

  const toggleMode = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return <ThemeContext.Provider value={{ mode, toggleMode }}>{children}</ThemeContext.Provider>;
}

/**
 * Hook to access theme context
 *
 * @returns ThemeContextValue containing mode and toggleMode
 * @throws Error if used outside of ThemeProvider
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
